import { apiGet, apiPostPut } from "./api_methods";
import { RECRUITER_APPLICATIONS_BASE } from "./endpoints";

/**
 * Get applications by job post (GET /v1/recruiter/applications/job/:id).
 * @param {string} jobPostId - Job post ID
 * @param {Object} params - Query params (skip, limit, isDeleted, clientId, searchFilter, hr, technical, status, score)
 * @returns {Promise<{ body, status }>}
 */
export async function getJobApplications(jobPostId, params = {}) {
  const q = {};
  if (params.skip != null) q.skip = params.skip;
  if (params.limit != null) q.limit = params.limit;
  if (params.isDeleted != null) q.isDeleted = params.isDeleted;
  if (params.clientId != null && params.clientId !== "")
    q.clientId = params.clientId;
  if (params.searchFilter != null && params.searchFilter !== "")
    q.searchFilter = params.searchFilter;
  if (params.hr != null && params.hr !== "") q.hr = params.hr;
  if (params.technical != null && params.technical !== "")
    q.technical = params.technical;
  if (params.status != null && params.status !== "") q.status = params.status;
  if (params.score != null && params.score !== "") q.score = params.score;

  const query = new URLSearchParams(q).toString();
  const path = query
    ? `${RECRUITER_APPLICATIONS_BASE}/job/${jobPostId}?${query}`
    : `${RECRUITER_APPLICATIONS_BASE}/job/${jobPostId}`;
  return apiGet(path);
}

/**
 * Get single application detail (GET /v1/recruiter/applications/:id).
 * @param {string} applicationId - Application ID
 * @param {Object} params - Query params for optional parameters (clientId)
 * @returns {Promise<{ body, status }>}
 */
export async function getApplicationDetail(applicationId, params = {}) {
  const q = {};
  if (params.clientId != null && params.clientId !== "")
    q.clientId = params.clientId;

  const query = new URLSearchParams(q).toString();
  const path = query
    ? `${RECRUITER_APPLICATIONS_BASE}/${applicationId}?${query}`
    : `${RECRUITER_APPLICATIONS_BASE}/${applicationId}`;
  return apiGet(path);
}

/**
 * Update single application detail (PUT /v1/recruiter/applications/:id).
 * @param {string} applicationId - Application ID
 * @param {Object} body - Fields to update.
 * @returns {Promise<{ body, status }>}
 */
export async function updateApplicationDetail(applicationId, body) {
  return apiPostPut(
    body,
    `${RECRUITER_APPLICATIONS_BASE}/${applicationId}`,
    "PUT",
  );
}

/**
 * Get application tracking data (GET /v1/recruiter/applications/tracking/:id).
 * @param {string} applicationId - Application ID
 * @returns {Promise<{ body, status }>}
 */
export async function getApplicationTracking(applicationId) {
  return apiGet(`${RECRUITER_APPLICATIONS_BASE}/tracking/${applicationId}`);
}

/**
 * Change an application's status/shortlist/hire/reject flags.
 * PUT /v1/recruiter/applications/status/:id
 * @param {string} applicationId - Application _id to update
 * @param {Object} payload - Fields to update (all optional):
 *   { status?: number, isShortlisted?: boolean, isRejected?: boolean, isHired?: boolean }
 * @returns {Promise<{ body, status }>}
 */
export async function changeApplicationStatus(applicationId, payload) {
  return apiPostPut(
    payload,
    `${RECRUITER_APPLICATIONS_BASE}/status/${applicationId}`,
    "PUT",
  );
}

/**
 * Get application status (GET /v1/recruiter/applications/status).
 * @param {Object} params - Query params (applicationId, interviewId, mockTestId)
 * @returns {Promise<{ body, status }>}
 */
export async function getApplicationStatus(params = {}) {
  const q = {};
  if (params.applicationId) q.applicationId = params.applicationId;
  if (params.interviewId) q.interviewId = params.interviewId;
  if (params.mockTestId) q.mockTestId = params.mockTestId;

  const query = new URLSearchParams(q).toString();
  const path = query
    ? `${RECRUITER_APPLICATIONS_BASE}/status?${query}`
    : `${RECRUITER_APPLICATIONS_BASE}/status`;
  return apiGet(path);
}

/**
 * Export applicants data for a company/job context.
 * POST /v1/recruiter/applications/company-applicants-data/:id
 * @param {string} recruiterId - Recruiter/User id used by legacy-compatible endpoint.
 * @param {Object} payload - Export filters.
 * @returns {Promise<{ body, status }>}
 */
export async function exportCompanyApplicantsData(recruiterId, payload) {
  return apiPostPut(
    payload,
    `${RECRUITER_APPLICATIONS_BASE}/company-applicants-data/${recruiterId}`,
    "POST",
  );
}
/**
 * Generate AI-powered applicant evaluation (GET /v1/recruiter/applications/evaluation/:id).
 * @param {string} applicationId - Application ID
 * @param {number} version - AI prompt version (defaults to 5)
 * @returns {Promise<{ body, status }>}
 */
export async function generateApplicantEvaluation(applicationId, version = 5) {
  return apiGet(
    `${RECRUITER_APPLICATIONS_BASE}/evaluation/${applicationId}?version=${version}`,
  );
}
